import { Evaluator, higherIsBetter, HumanEvaluator } from "@/types/app/evaluator";
import { EvaluatorLog, Log } from "@/types/app/log";
import { renderAggregateValue } from "@components/llama/Evaluations/EvaluationResult";
import { useRequireAuth } from "@/lib/use-auth";
import { createEvaluatorLogAndFetchLog } from "@/services/evaluators.service";
import { TextInput } from "@components/molecules/FormikInput";
import React, { useState } from "react";
import { None } from "@components/llama/Tables/Logs/LogCells";
import { StatValue } from "@components/llama/Evaluations/Evaluation/EvaluationReport/Stats/StatGrid";

interface Props {
  evaluator: Evaluator;
  latestFeedback: Log | null;
  compareEvaluatorLog?: Log | null;
  setAddingFeedback: (addingFeedback: boolean) => void;
  onAddFeedback: (evaluatorLog: EvaluatorLog) => void;
  log: Log;
  placeholder?: string;
  onError: (error: string) => void;
  className?: string;
}

export const FEEDBACK_NUMBER_DATA_TESTID_TAG = "TAG_TO_FIND_FEEDBACK_NUMBER_COMPONENT";

export const FeedbackNumber = ({
  evaluator,
  latestFeedback,
  setAddingFeedback,
  onAddFeedback,
  placeholder,
  compareEvaluatorLog = null,
  log,
  onError,
  className,
}: Props) => {
  const judgment: number | null = latestFeedback && "judgment" in latestFeedback ? latestFeedback.judgment : null;
  const { user } = useRequireAuth();
  const [validationError, setValidationError] = useState<string | null>(null);

  const { displayValue, value } = renderAggregateValue(evaluator, judgment, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // used to compare value with first column
  const { displayValue: _, value: compareValue } = renderAggregateValue(
    evaluator,
    compareEvaluatorLog && "judgment" in compareEvaluatorLog ? compareEvaluatorLog.judgment : null,
    {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  );

  const [prettyValue, setPrettyValue] = useState(displayValue);
  const editable = evaluator.spec.evaluator_type === "human";
  let min: number | null = null;
  let max: number | null = null;
  let step: number | null = null;

  if (editable) {
    ({ min, max, step } = (evaluator as HumanEvaluator).spec.number_limits || {
      min: null,
      max: null,
      step: null,
    });
  }

  return (
    (editable && (
      <TextInput
        data-testid={FEEDBACK_NUMBER_DATA_TESTID_TAG}
        type="number"
        name=""
        error={validationError}
        errorClasses={"text-11-14 text-text-danger-2"}
        placeholder={placeholder ?? "None"}
        step={step ?? undefined}
        min={min ?? undefined}
        max={max ?? undefined}
        label=""
        className={className}
        suppressAddOnRightPadding={true}
        containerClasses={"flex-grow "}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === "Escape") {
            event.currentTarget.blur();
          }
        }}
        value={typeof prettyValue === "number" || typeof prettyValue === "string" ? prettyValue : undefined}
        onBlur={async (event) => {
          const value = parseFloat(event.target.value);
          if (isNaN(value)) {
            // handle empty string case, don't set a validation error as it's not an error
            return;
          }
          if (min !== null && value < min) {
            setValidationError(`Value must be more than ${min}.`);
            return;
          } else if (max !== null && value > max) {
            setValidationError(`Value must be less than ${max}.`);
            return;
          } else if (step !== null && step !== undefined && min !== null && (value - min) % step !== 0) {
            setValidationError(`Value must be in steps of ${step}. Min is ${min}, Max is ${max}`);
            return;
          }
          setAddingFeedback(true);
          setPrettyValue(value);
          try {
            const createdLog = await createEvaluatorLogAndFetchLog(
              {
                id: evaluator.id,
                parent_id: log.id,
                judgment: isNaN(value) ? null : value,
                user: user?.email_address,
              },
              evaluator.version_id,
            );
            onAddFeedback(createdLog);
          } catch (error) {
            setPrettyValue(judgment);
            onError("Failed to update field. Please try again");
          } finally {
            setAddingFeedback(false);
          }
        }}
        onChange={(event) => {
          const value = parseFloat(event.target.value);
          setPrettyValue(value);
          setValidationError(null);
        }}
      />
    )) || (
      <span>
        <div className="flex max-w-[542px] justify-start whitespace-pre-wrap pr-20 text-13-18 text-oldgray-800">
          {judgment == null ? (
            <None className={"text-12-14 !text-text-base-4"} />
          ) : (
            <StatValue
              value={value as number}
              className="!border-none !px-0"
              displayValue={prettyValue as string}
              comparison={
                log.id === compareEvaluatorLog?.id || !compareValue
                  ? null
                  : { value: compareValue, higherIsBetter: higherIsBetter(evaluator) }
              }
            />
          )}
        </div>
      </span>
    )
  );
};
